//* Library imports
import { motion } from "framer-motion";

//* Component imports
import { Loading } from "../../../../../../component/common/Loading";
import { ResourceQuestionActions } from "./resource-question-actions";
  
//? Styles imports
import badgeStyles from "../../../../../../assets/css/modern-status-badges.module.scss";

type ResourceQuestionField = {
  name: string;
  header: string;
  type: "text" | "date" | "boolean" | "status";
};

type ResourceQuestionTableProps = {
  fields: ResourceQuestionField[];
  items: any[];
  loading: boolean;
  notFoundMessage: string;
  onEditResourceQuestion?: (id: string) => void;
  onDeleteResourceQuestion?: (id: string) => void;
  onViewResourceQuestion?: (id: string) => void;
  hideEdit?: boolean;
  hideDelete?: boolean;
  hideView?: boolean;
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
};

export function ResourceQuestionTable({
  fields,
  items,
  loading,
  notFoundMessage,
  onEditResourceQuestion,
  onDeleteResourceQuestion,
  onViewResourceQuestion,
  hideEdit = false,
  hideDelete = false,
  hideView = false,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  onPageChange,
}: ResourceQuestionTableProps) {  const getStatusBadge = (status: string) => {

    const statusMap: { [k: string]: { className: string; text: string } } = {
      active: { className: badgeStyles.active, text: "Ativo" },
      inactive: { className: badgeStyles.inactive, text: "Expirado" },
      pending: { className: badgeStyles.pending, text: "Próximo ao Vencimento" },
      ativo: { className: badgeStyles.active, text: "Ativo" },
      inativo: { className: badgeStyles.inactive, text: "Inativo" },
      pendente: { className: badgeStyles.pending, text: "Pendente" },
      concluido: { className: badgeStyles.completed, text: "Concluído" },
      em_andamento: { className: badgeStyles.inProgress, text: "Em Andamento" },
      aprovado: { className: badgeStyles.approved, text: "Aprovado" },
      rejeitado: { className: badgeStyles.rejected, text: "Rejeitado" },
      rascunho: { className: badgeStyles.draft, text: "Rascunho" },
    };
    const key = status?.toLowerCase() || "";

    const info = statusMap[key] || {
      className: badgeStyles.default,
      text: status || "N/A",
    };

    return (
      <span
        className={`${badgeStyles.modernBadge} ${info.className} ${badgeStyles.newBadge}`}
        title={`Status: ${info.text}`}
      >
        {info.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Loading data-testid="loading-spinner" />
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="text-center py-5 my-4"
      >
        <div className="text-muted">
          <div className="mb-4">
            <i className="fas fa-inbox fa-3x text-secondary opacity-50"></i>
          </div>
          <h5 className="fw-normal text-secondary mb-0">{notFoundMessage}</h5>
        </div>
      </motion.div>
    );
  }

  const renderPagination = () => {
    if (!onPageChange || totalPages <= 1) return null;
    const startItem = (currentPage - 1) * 10 + 1;
    const endItem = Math.min(currentPage * 10, totalItems);
    return (
      <div className="mt-4 d-flex justify-content-between align-items-start p-3 bg-light rounded-3">
        <p className="small text-muted mb-0" style={{ fontSize: "0.875rem" }}>
          Mostrando <span className="fw-semibold text-dark">{startItem}</span> a{" "}
          <span className="fw-semibold text-dark">{endItem}</span> de{" "}
          <span className="fw-semibold text-dark">{totalItems}</span> resultados
        </p>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-primary btn-sm px-4 py-2 rounded-pill"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage <= 1}
            style={{ fontSize: "0.875rem", fontWeight: 500 }}
          >
            <i className="fas fa-chevron-left me-1" />
            Anterior
          </button>
          <button
            className="btn btn-outline-primary btn-sm px-4 py-2 rounded-pill"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages}
            style={{ fontSize: "0.875rem", fontWeight: 500 }}
          >
            Próximo
            <i className="fas fa-chevron-right ms-1" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <div
        className="table-responsive shadow-sm rounded-3 border-0"
        style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}
      >
        <table className="table table-hover mb-0 text-center align-middle">
          <thead className="bg-light border-0">
            <tr>
              {fields.map((field, idx) => (
                <th
                  key={idx}
                  className={`px-4 py-4 border-0 fw-semibold text-dark align-middle ${
                    idx === 0 ? "text-start" : "text-center"
                  }`}
                  style={{ fontSize: "0.875rem", letterSpacing: "0.025em" }}
                >
                  {field.header}
                </th>
              ))}
              <th
                className="px-4 py-4 border-0 fw-semibold text-dark align-middle text-center"
                style={{
                  fontSize: "0.875rem",
                  letterSpacing: "0.025em",
                  width: 120,
                }}
              >
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, rowIndex) => (
              <motion.tr
                key={rowIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: rowIndex * 0.05, duration: 0.3 }}
                className="border-0"
                style={{ borderBottom: "1px solid #f1f3f4" }}
              >
                {fields.map((field, colIndex) => {
                  const isFirst = colIndex === 0;
                  const baseClasses = `px-4 py-4 border-0 align-middle ${
                    isFirst ? "text-start" : "text-center"
                  }`;
                  const fontSize = isFirst ? "0.9rem" : "0.875rem";

                  switch (field.type) {
                    case "status":
                      return (
                        <td key={colIndex} className={baseClasses}>
                          {getStatusBadge(item[field.name])}
                        </td>
                      );                    case "date":
                      return (
                        <td
                          key={colIndex}
                          className={baseClasses}
                          style={{ fontSize }}
                        >
                          {item[field.name]?.human?.date || "-"}
                        </td>
                      );
                    case "boolean":
                      return (
                        <td
                          key={colIndex}
                          className={baseClasses}
                          style={{ fontSize }}
                        >
                          <span
                            className={`${badgeStyles.modernBadge} ${badgeStyles.compact} ${
                              item[field.name] ? badgeStyles.active : badgeStyles.inactive
                            }`}
                            title={`Valor: ${item[field.name] ? "Sim" : "Não"}`}
                          >
                            {item[field.name] ? "Sim" : "Não"}
                          </span>
                        </td>
                      );
                    default:
                      return (
                        <td
                          key={colIndex}
                          className={baseClasses}
                          style={{ fontSize }}
                        >
                          <span>{item[field.name]?.toString() ?? "N/A"}</span>
                        </td>
                      );
                  }
                })}
                <td className="px-4 py-4 text-center align-middle border-0">
                  <ResourceQuestionActions
                    resourceId={item.id}
                    onViewClick={
                      onViewResourceQuestion
                        ? () => onViewResourceQuestion(item.id)
                        : undefined
                    }
                    onEditClick={
                      onEditResourceQuestion
                        ? () => onEditResourceQuestion(item.id)
                        : undefined
                    }
                    onDeleteClick={
                      onDeleteResourceQuestion
                        ? () => onDeleteResourceQuestion(item.id)
                        : undefined
                    }
                    hideEdit={hideEdit}
                    hideDelete={hideDelete}
                    hideView={hideView}
                  />
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {renderPagination()}
    </>
  );
}
